import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { components } from 'react-select';

import MainFont from 'ui/typography/mainFont';

import { ClickOutside } from '..';
import theme from '../../theme';
import Checkbox from '../Checkbox';
import Icon from '../Icon';
import Tooltip from '../Tooltip';

import {
  ArrowContainer,
  DownArrow,
  IconInfo,
  OptionContainer,
  Placeholder,
  ResetWrapper,
  SelectWrapper,
  UpArrow,
  WrapperControl
} from './style';

const Option = props => (
  <OptionContainer>
    <components.Option {...props}>
      {!props.selectProps.paginationMenu && (
        <Checkbox checked={props.isSelected} />
      )}
      <MainFont configuration={{ fontSize: 12 }}>
        {props?.label || props?.data || ''}
      </MainFont>
    </components.Option>
  </OptionContainer>
);

const DropdownIndicatorDouble = props => (
  <components.DropdownIndicator {...props}>
    <ArrowContainer>
      <UpArrow />
      <DownArrow />
    </ArrowContainer>
  </components.DropdownIndicator>
);

const DropdownIndicatorSingle = props => (
  <components.DropdownIndicator {...props}>
    <ArrowContainer>
      <Icon
        type="icon-chevron-up"
        rotate={180}
        color={
          props.selectProps.variant === 'login'
            ? theme().colors.white
            : theme().colors.mainBlue
        }
        size={20}
      />
    </ArrowContainer>
  </components.DropdownIndicator>
);

const ValueContainer = ({ children, ...props }) => {
  const value = props.getValue();
  return (
    <components.ValueContainer {...props}>
      <Placeholder
        hasValue={props.hasValue}
        hide={props.selectProps.hidePlaceholderOnSelection}
        variant={props.selectProps.variant}
      >
        <MainFont
          configuration={{
            fontSize: 14
          }}
        >
          {props?.selectProps?.placeholder}
        </MainFont>
      </Placeholder>
      {value.length > 0 && (
        <MainFont
          configuration={{
            fontWeight: props.selectProps.hidePlaceholderOnSelection
              ? 400
              : !props.selectProps.hidePlaceholderOnSelection &&
                props.selectProps.variant !== 'login' &&
                props.selectProps.variant !== 'editaccountdata'
              ? 600
              : 'normal',
            fontSize: 14,
            lineHeight: 1.71
          }}
        >
          {`${Object.keys(value[0]).length > 0 ? value[0].label : value[0]}${
            Array.isArray(value) && value.length > 1 ? '...' : ''
          }`}
        </MainFont>
      )}
    </components.ValueContainer>
  );
};

const Menu = props => {
  const { t } = useTranslation();

  return (
    <>
      <components.Menu {...props}>
        {props.selectProps.resetSelection && (
          <ResetWrapper onClick={props.selectProps.clearSelected}>
            <MainFont type="resetFilterText">
              {typeof props.selectProps.resetSelection === 'string'
                ? t(props.selectProps.resetSelection)
                : t('reset_filter')}
            </MainFont>
          </ResetWrapper>
        )}
        {props.children}
      </components.Menu>
    </>
  );
};

const Control = props => (
  <WrapperControl
    ref={props.innerRef}
    onClick={() =>
      props.selectProps.setMenuIsOpen(!props.selectProps.menuIsOpen)
    }
    hasValue={props.hasValue}
    paginationMenu={props.selectProps.paginationMenu}
    variant={props.selectProps.variant}
  >
    <components.Control {...props} />
  </WrapperControl>
);

const DropDown = ({
  placeholder,
  options = [],
  isMulti,
  defaultIndex,
  onChange,
  maxMenuHeight,
  menuWidth,
  checkBoxMenu,
  paginationMenu,
  detachedOptions,
  onMenuOpen,
  onMenuClose,
  resetSelection,
  variant,
  tooltipText,
  noMinWidth,
  hidePlaceholderOnSelection,
  hideSelectedOptions = false
}) => {
  const [optionSelected, setOptionSelected] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const handleChange = selected => {
    setOptionSelected(selected);
    if (typeof onChange === 'function') {
      onChange(selected);
    }
  };

  const clearSelected = () => {
    setOptionSelected(null);
    if (typeof onChange === 'function') {
      onChange(null);
    }
  };

  useEffect(() => {
    if (defaultIndex) {
      if (Array.isArray(defaultIndex)) {
        setOptionSelected(options.filter((_, i) => defaultIndex.includes(i)));
      } else {
        setOptionSelected(options[defaultIndex]);
      }
    }
    //eslint-disable-next-line
  }, [JSON.stringify(defaultIndex)]);

  return (
    <ClickOutside callback={() => setMenuIsOpen(false)}>
      <SelectWrapper
        classNamePrefix="Select"
        isClearable={false}
        placeholder={placeholder}
        isSearchable={false}
        closeMenuOnSelect={!isMulti}
        isMulti={isMulti}
        hideSelectedOptions={hideSelectedOptions}
        options={options}
        allowSelectAll
        checkBoxMenu={checkBoxMenu}
        components={{
          Option,
          ValueContainer,
          Menu,
          DropdownIndicator: paginationMenu
            ? DropdownIndicatorDouble
            : DropdownIndicatorSingle,
          Control
        }}
        value={optionSelected}
        onChange={handleChange}
        maxMenuHeight={maxMenuHeight}
        menuWidth={menuWidth}
        paginationMenu={paginationMenu}
        detachedOptions={detachedOptions}
        menuIsOpen={menuIsOpen}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        clearSelected={clearSelected}
        resetSelection={resetSelection}
        getOptionLabel={option => option.label || option}
        getOptionValue={option => option.value || option}
        setMenuIsOpen={setMenuIsOpen}
        variant={variant}
        hidePlaceholderOnSelection={hidePlaceholderOnSelection}
      />
      {tooltipText && (
        <>
          <IconInfo
            data-tip={tooltipText}
            data-offset="{'top': 0, 'right':-55 }"
            data-for={tooltipText}
          >
            <Icon type="icon-info" size="24" color={theme().colors.mainBlue} />
          </IconInfo>
          <Tooltip id={tooltipText} />
        </>
      )}
    </ClickOutside>
  );
};

DropDown.defaultProps = {
  variant: 'default',
  noMinWidth: false
};

DropDown.propTypes = {
  placeholder: PropTypes.string,
  options: PropTypes.array,
  isMulti: PropTypes.bool,
  defaultIndex: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.number
  ]),
  onChange: PropTypes.func,
  maxMenuHeight: PropTypes.number,
  menuWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  checkBoxMenu: PropTypes.bool,
  paginationMenu: PropTypes.bool,
  detachedOptions: PropTypes.bool,
  onMenuOpen: PropTypes.func,
  onMenuClose: PropTypes.func,
  resetSelection: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  variant: PropTypes.string,
  tooltipText: PropTypes.string,
  hidePlaceholderOnSelection: PropTypes.bool,
  hideSelectedOptions: PropTypes.bool,
  noMinWidth: PropTypes.bool
};

export default DropDown;
